import { ref } from 'vue'
import { loginRequestSchema } from '@/contracts/auth.contract'
import { useAuthStore } from '@/stores/auth'
import { useAuthForm } from './useAuthForm'
import { useCaptcha } from './useCaptcha'

/**
 * 登入表單的狀態與提交。
 *
 * 抽出來是因為登入現在有**兩個入口**：`/login` 這一頁，以及被閘門擋下時
 * 就地彈出的對話框（見 `useAuthGate`）。兩邊的欄位、本地驗證、失敗後換
 * 驗證碼完全一樣 —— 複製一份的下場就是 T1 的
 * `MemberLogin` / `MemberRegister` / `MemberSignInUp` 三份各自長歪。
 *
 * 差別只有「成功之後去哪」，那件事留給呼叫端。
 */
export function useLoginForm(options: { immediateCaptcha?: boolean } = {}) {
  const auth = useAuthStore()
  const form = useAuthForm()
  const captcha = useCaptcha({ immediate: options.immediateCaptcha ?? true })

  const username = ref('')
  const password = ref('')
  const captchaAnswer = ref('')

  /**
   * 送出前先用**與後端同一份** zod schema 做本地驗證。
   * 省掉一次註定失敗的往返，也保證兩邊規則不會漂移。
   */
  function validateLocally(): boolean {
    const result = loginRequestSchema.safeParse({
      username: username.value,
      password: password.value,
      captchaId: captcha.captchaId.value,
      captchaAnswer: captchaAnswer.value,
    })
    if (result.success) return true

    form.setFieldErrors(
      Object.fromEntries(
        result.error.issues.map((issue) => [
          String(issue.path[0] ?? '_'),
          `validation.${issue.message}`,
        ]),
      ),
    )
    return false
  }

  /** 回傳 `true` 代表已登入。失敗的呈現都在 `useAuthForm` 裡 */
  async function submit(): Promise<boolean> {
    if (!validateLocally()) return false

    const ok = await form.submit(() =>
      auth.login({
        username: username.value,
        password: password.value,
        captchaId: captcha.captchaId.value,
        captchaAnswer: captchaAnswer.value,
      }),
    )

    if (!ok) {
      // 驗證碼是一次性的，這張已經被後端作廢了 ——
      // 不換新的話，使用者再按一次必定收到「驗證碼無效」，
      // 卻會以為是自己看錯數字
      captchaAnswer.value = ''
      void captcha.refresh()
      return false
    }

    return true
  }

  /**
   * 清空整份表單並換一張新的驗證碼。
   *
   * 對話框版本每次開啟都要呼叫：它不會被卸載，所以上一次留下的帳號、
   * 紅字錯誤、以及那張早就過期的驗證碼都還在畫面上。
   */
  function reset(): void {
    username.value = ''
    password.value = ''
    captchaAnswer.value = ''
    form.reset()
    void captcha.refresh()
  }

  return { username, password, captchaAnswer, captcha, form, submit, reset }
}
